// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */

@Component({
  selector: "app-edit-note-dialog",
  templateUrl: "./edit-note-dialog.component.html",
  styleUrls: ["./edit-note-dialog.component.scss"],
})
export class BalanceEditNoteDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit phonebook contact form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit phonebook contact form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<BalanceEditNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private balancesService: BalancesService,
    public confirmDialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      this.fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "textarea",
            key: "text",
            defaultValue: this.data.text,
            templateOptions: {
              label: this.translateService.instant("properties.structures.note"),
              placeholder: this.translateService.instant(
                "properties.structures.note"
              ),
              required: true,
              minLength: 0,
              maxLength: 1000,
              rows: 4,
            },
          },
        ],
      });
    }
  }

  
  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
   onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    this.subscriptions.push(
      this.balancesService.updateNote(
        this.data.balanceId,
        this.data.noteId,
        model,
      )
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            // The API wrapper exposes the success flag as `status` (V2 convention,
            // used across the rest of this module); some endpoints also report it
            // as `success`. Accept both so the result is detected reliably.
            if (res && (res.status === true || res.success === true)) {
              this.toastrService.success(this.translateService.instant('common.words.operation_successful'));
              this.onClose(res);
              return;
            }
            this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
   onClose(data?: any): void {
    this.dialogRef.close(data);
  }
}
